import axios from 'axios';

/**
 * Résumé objects are private. Reaching one means asking the BFF to sign for it.
 *
 * In Common because three surfaces need it: the importer, the job-seeker workspace and the
 * application workspace. It lived under JobSeekersImport, so the application workspace did not
 * find it and linked the raw S3 URL instead -- which is an AccessDenied for every résumé uploaded
 * since the bucket went private, and is what an HR hit on "view documents". A helper filed under
 * one feature is a helper the next feature reimplements or skips.
 *
 * The stored `fileUrl` is a public-form S3 URL, built client-side at upload time as
 * `https://<bucket>.s3.<region>.amazonaws.com/<key>`. It has never been a working URL for anything
 * uploaded since the bucket policy tightened: `trymintly-resume` is `allowPublic: false`, so the
 * BFF drops the requested public-read ACL and the object is written private. Fetching that URL
 * anonymously returns 403. Objects from before the policy change carry `AllUsers READ` and still
 * resolve, which is exactly why this reads as "preview is broken for some rows" rather than as a
 * policy change.
 *
 * The fix is not to make the bucket public again. A résumé is personal data and private is the
 * correct posture; the historic world-readable objects are the older problem. So the URL is treated
 * as what it actually is -- a record of the bucket and key -- and the object is fetched through the
 * BFF's presigned-download route, which holds the credentials.
 */
const RESUME_BUCKET = 'trymintly-resume';
const RESUME_REGION = 'ap-south-1';

/**
 * The public-form S3 URL to record on a row after uploading a résumé.
 *
 * The write side of {@link parseS3Url}, and here beside it because the two have to agree about
 * both the bucket and the spelling of the key. They did not: three call sites in the importer and
 * one in the job-seeker workspace each pasted the key onto the host by hand, unencoded.
 *
 * The key is a timestamp joined to whatever the candidate named their CV, so it routinely carries
 * spaces and brackets: `external_1787834921714_Saanvi CV.pdf`,
 * `external_1787834766830_Naukri_Kunalbhogale[5y_0m].pdf`. Concatenated raw those are not legal
 * URLs, and while this parser is lenient enough not to care, Java's is not: the backend's
 * `S3ObjectReader.parse` called `URI.create`, got "Illegal character in path" and treated the row
 * as an object it could not sign for, so the fetch fell back to an anonymous GET of a private
 * object and every such résumé failed to extract. That is fixed on both sides; this is the half
 * that stops new rows carrying a `fileUrl` that is not a URL.
 *
 * `encodeURIComponent`, not `encodeURI`: these keys are a single flat segment with no path
 * structure to preserve, and `encodeURI` leaves `[` and `]` alone.
 */
export function resumeObjectUrl(bucketKey: string): string {
  return `https://${RESUME_BUCKET}.s3.${RESUME_REGION}.amazonaws.com/${encodeURIComponent(bucketKey)}`;
}

/** Bucket and key from a stored S3 URL, or null when it is not one of ours. */
export function parseS3Url(
  fileUrl: string | null | undefined,
): { bucket: string; key: string } | null {
  if (!fileUrl) return null;
  let url: URL;
  try {
    url = new URL(fileUrl);
  } catch {
    return null;
  }
  // Virtual-hosted style is what the uploader writes: <bucket>.s3.<region>.amazonaws.com/<key>
  const host = url.hostname;
  const match = /^([a-z0-9.-]+)\.s3[.-][a-z0-9-]*\.?amazonaws\.com$/i.exec(host);
  if (!match) return null;
  // decodeURIComponent, because the key was percent-encoded into the URL and the signing route
  // wants the real key. A résumé filename with a space is the common case, not an edge one.
  const key = decodeURIComponent(url.pathname.replace(/^\/+/, ''));
  if (!key) return null;
  return { bucket: match[1], key };
}

/**
 * A short-lived URL that actually resolves, or null when one cannot be produced.
 *
 * Falls back to the original URL for anything not in our buckets, so an externally hosted résumé
 * keeps working. Returns null rather than throwing: every caller here is an operator pressing a
 * button, and a thrown error in a click handler is a blank screen.
 */
export async function resolveResumeUrl(
  fileUrl: string | null | undefined,
): Promise<string | null> {
  if (!fileUrl) return null;
  const parsed = parseS3Url(fileUrl);
  if (!parsed) return fileUrl;

  try {
    const resp = await axios.get(
      `/api/s3/buckets/${encodeURIComponent(parsed.bucket)}/download/${encodeURIComponent(parsed.key)}`,
    );
    return resp.data?.url ?? null;
  } catch {
    return null;
  }
}

export { RESUME_BUCKET };
